import { RadioGroup, RadioGroupProps } from '@onegov/react-component-library';
import { type FieldPath, type FieldValues, useController } from 'react-hook-form';
import { useFieldErrorMessage } from '../hooks';
import { type FormFieldProps } from '../types';
import { remapRequiredRule } from './remapRequiredRule';

export type FormRadioGroupProps<
  TValue extends unknown,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = FormFieldProps<TFieldValues, TName>
  // Make the onChange property optional
  & Partial<Pick<RadioGroupProps<TValue>, 'onChange'>>
  & Omit<RadioGroupProps<TValue>,
    | 'value'
    | 'errorMessage'
  >;

export const FormRadioGroup = <
  TValue extends unknown,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  { id, name, options, rules, shouldUnregister, onChange, errorMode, ...radioGroupProps }: FormRadioGroupProps<TValue, TFieldValues, TName>
) => {
  const {
    field: { name: fieldName, onChange: onFieldChange, ...fieldProps },
  } = useController({
    name,
    rules: remapRequiredRule<TFieldValues, TName>(options, rules),
    shouldUnregister,
  });

  const errorMessage = useFieldErrorMessage(fieldName, errorMode);

  const handleChange = (value: TValue) => {
    onFieldChange(value);
    onChange && onChange(value);
  };

  return (
    <RadioGroup<TValue>
      {...radioGroupProps}
      {...fieldProps}
      name={fieldName}
      options={options}
      onChange={handleChange}
      errorMessage={errorMessage}
    />
  );
}